/**
 * MobileInspectorSimulator Component
 * Simulates a mobile inspector workflow for capturing GPS-tagged photos
 * Ported from marketing-site MobileInspectorSimulator.jsx to TSX
 * Matches WF-7 wireframe: device frame, GPS, capture flow
 */

import { useState, useEffect, useCallback } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';
import type { Finding } from '../types/api';

export interface MobileInspectorSimulatorProps {
  isOpen: boolean;
  onClose: () => void;
  finding: Finding;
  wineryName?: string;
  onSubmit?: (photos: InspectionPhoto[]) => void;
}

export interface InspectionPhoto {
  id: string;
  timestamp: string;
  gps: { latitude: number; longitude: number; accuracy: number };
  source: 'mobile_inspection';
}

type SimStep = 'initial' | 'preview' | 'captured' | 'uploading' | 'success';

// Napa Valley GPS coordinates range
const NAPA_CENTER = { lat: 38.5025, lng: -122.2654 };
const GPS_VARIANCE = 0.01;

function randomGPS() {
  return {
    latitude: NAPA_CENTER.lat + (Math.random() - 0.5) * GPS_VARIANCE,
    longitude: NAPA_CENTER.lng + (Math.random() - 0.5) * GPS_VARIANCE,
    accuracy: 5 + Math.random() * 10,
  };
}

export function MobileInspectorSimulator({
  isOpen,
  onClose,
  finding,
  wineryName = 'Winery',
  onSubmit,
}: MobileInspectorSimulatorProps) {
  const [step, setStep] = useState<SimStep>('initial');
  const [photos, setPhotos] = useState<InspectionPhoto[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [gps] = useState(randomGPS);

  useEffect(() => {
    if (!isOpen) {
      setStep('initial');
      setPhotos([]);
      setUploadProgress(0);
    }
  }, [isOpen]);

  const capturePhoto = useCallback(() => {
    setStep('preview');
    // Simulate camera preview delay
    setTimeout(() => {
      const photo: InspectionPhoto = {
        id: crypto.randomUUID(),
        timestamp: new Date().toISOString(),
        gps: randomGPS(),
        source: 'mobile_inspection',
      };
      setPhotos((prev) => [...prev, photo]);
      setStep('captured');
    }, 800);
  }, []);

  const submitInspection = useCallback(() => {
    setStep('uploading');
    let progress = 0;
    const interval = setInterval(() => {
      progress += 10;
      setUploadProgress(progress);
      if (progress >= 100) {
        clearInterval(interval);
        setStep('success');
        setTimeout(() => {
          onSubmit?.(photos);
          onClose();
        }, 1000);
      }
    }, 120);
  }, [photos, onSubmit, onClose]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Mobile Inspector" size="sm">
      {/* Device frame */}
      <div className="mx-auto w-[280px] bg-gray-900 rounded-[2rem] p-3 shadow-2xl relative">
        {/* Demo watermark */}
        <div
          className="absolute inset-0 flex items-center justify-center pointer-events-none z-10"
          aria-hidden="true"
        >
          <span className="text-4xl font-black text-gray-500/20 rotate-[-30deg] select-none tracking-widest">
            DEMO
          </span>
        </div>
        {/* Notch */}
        <div className="w-20 h-5 bg-gray-900 rounded-b-xl mx-auto -mt-1 mb-2" />

        {/* Screen */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl overflow-hidden min-h-[400px] flex flex-col">
          {/* Header */}
          <div className="bg-gray-900 text-white px-4 py-3">
            <p className="text-xs font-medium opacity-80">INSPECTION TASK</p>
            <p className="text-sm font-semibold truncate">{finding.title}</p>
            <p className="text-xs opacity-70 truncate">{wineryName}</p>
          </div>

          {/* GPS bar */}
          <div className="bg-gray-100 dark:bg-slate-700 px-3 py-1.5 flex items-center gap-1.5 border-b dark:border-slate-600">
            <svg className="w-3 h-3 text-gray-500 dark:text-slate-400" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
            </svg>
            <span className="text-[10px] text-gray-600 dark:text-slate-400 font-mono">
              {gps.latitude.toFixed(4)}, {gps.longitude.toFixed(4)}
            </span>
            <span className="text-[10px] text-gray-600 dark:text-slate-400 ml-auto">GPS Locked</span>
          </div>

          {/* Content area */}
          <div className="flex-1 flex flex-col items-center justify-center p-4">
            {step === 'initial' && (
              <>
                <div className="w-full aspect-[4/3] bg-gray-200 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-12 h-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                      d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <p className="text-xs text-gray-500 dark:text-slate-400 text-center mb-3">
                  Photos captured: {photos.length}
                </p>
                <button
                  onClick={capturePhoto}
                  className="w-14 h-14 bg-gray-900 rounded-full flex items-center justify-center hover:bg-gray-800 transition-colors shadow-lg"
                >
                  <div className="w-10 h-10 border-2 border-white rounded-full" />
                </button>
                <p className="text-[10px] text-gray-400 mt-2">Take Photo</p>
              </>
            )}

            {step === 'preview' && (
              <div className="w-full aspect-[4/3] bg-gray-800 rounded-lg flex items-center justify-center animate-pulse">
                <svg className="w-8 h-8 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>
            )}

            {step === 'captured' && (
              <>
                <div className="w-full aspect-[4/3] bg-gray-100 dark:bg-slate-700 rounded-lg flex items-center justify-center mb-3 border-2 border-gray-300 dark:border-slate-600">
                  <svg className="w-10 h-10 text-gray-500 dark:text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-xs text-gray-600 dark:text-slate-400 mb-1">
                  {photos.length} photo{photos.length !== 1 ? 's' : ''} captured
                </p>
                <p className="text-[10px] text-gray-400 mb-4">
                  {new Date(photos[photos.length - 1]?.timestamp || '').toLocaleTimeString()}
                </p>
                <div className="flex gap-2 w-full">
                  <button
                    onClick={capturePhoto}
                    className="flex-1 py-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300 rounded-lg text-xs font-medium hover:bg-gray-200 dark:hover:bg-slate-600"
                  >
                    Take Another
                  </button>
                  <button
                    onClick={submitInspection}
                    className="flex-1 py-2 bg-gray-900 text-white rounded-lg text-xs font-medium hover:bg-gray-800"
                  >
                    Submit ({photos.length})
                  </button>
                </div>
              </>
            )}

            {step === 'uploading' && (
              <div className="w-full text-center">
                <p className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-3">Uploading...</p>
                <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                  <div
                    className="bg-gray-900 h-2 rounded-full transition-all duration-100"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 dark:text-slate-400">{uploadProgress}%</p>
              </div>
            )}

            {step === 'success' && (
              <div className="text-center">
                <svg className="w-16 h-16 text-gray-500 dark:text-slate-400 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm font-medium text-gray-900 dark:text-slate-100">Inspection Complete</p>
                <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                  {photos.length} photo{photos.length !== 1 ? 's' : ''} submitted
                </p>
              </div>
            )}
          </div>

          {/* GPS accuracy bar */}
          <div className="bg-gray-50 dark:bg-slate-800 px-3 py-1 text-center border-t dark:border-slate-700">
            <span className="text-[9px] text-gray-400">
              Accuracy: {gps.accuracy.toFixed(1)}m
            </span>
          </div>
        </div>

        {/* Home indicator */}
        <div className="w-24 h-1 bg-gray-600 rounded-full mx-auto mt-2" />
      </div>
    </Modal>
  );
}

export default MobileInspectorSimulator;
